import { useState } from 'react';
import { Heart, MessageCircle, Share2, BadgeCheck } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CommentsModal from './CommentsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PostCard = ({ post, onLike }) => {
    const [liked, setLiked] = useState(post.liked_by_user);
    const [likesCount, setLikesCount] = useState(post.likes_count);
    const [showHeartAnimation, setShowHeartAnimation] = useState(false);
    const [showComments, setShowComments] = useState(false);
    const navigate = useNavigate();

    const handleLike = async () => {
        try {
            const response = await axios.post(`${API}/posts/${post.id}/like`);
            setLiked(response.data.liked);
            setLikesCount(prev => response.data.liked ? prev + 1 : prev - 1);
            
            if (response.data.liked) {
                setShowHeartAnimation(true);
                setTimeout(() => setShowHeartAnimation(false), 600);
            }
        } catch (error) {
            console.error('Error liking post:', error);
        }
    };

    const handleDoubleTap = () => {
        if (!liked) {
            handleLike();
        } else {
            setShowHeartAnimation(true);
            setTimeout(() => setShowHeartAnimation(false), 600);
        }
    };

    const formatCount = (count) => {
        if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
        if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
        return count.toString();
    };

    return (
        <>
            <div 
                className="snap-item w-full relative bg-black"
                style={{ height: 'calc(100dvh - 3.5rem - 5rem)' }}
                data-testid={`post-card-${post.id}`}
            >
                {/* Background Image */}
                <div 
                    className="absolute inset-0 cursor-pointer"
                    onDoubleClick={handleDoubleTap}
                >
                    <img
                        src={post.media_url}
                        alt={post.caption}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                    {/* Gradient Overlay */}
                    <div className="absolute inset-0 gradient-overlay" />
                </div>

                {/* Heart Animation */}
                <AnimatePresence>
                    {showHeartAnimation && (
                        <motion.div
                            initial={{ scale: 0, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0, opacity: 0 }}
                            className="absolute inset-0 flex items-center justify-center pointer-events-none z-10"
                        >
                            <Heart className="w-24 h-24 text-red-500 fill-red-500 drop-shadow-2xl" />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Content Overlay - positioned above bottom nav safe area */}
                <div className="absolute bottom-4 left-0 right-16 px-4 z-10">
                    {/* User Info */}
                    <button 
                        onClick={() => navigate(`/profile/${post.user_id}`)}
                        className="flex items-center gap-2 mb-3"
                        data-testid={`post-user-${post.id}`}
                    >
                        <Avatar className="w-10 h-10 border-2 border-white/20">
                            <AvatarImage src={post.user_avatar} alt={post.username} />
                            <AvatarFallback className="bg-primary text-white">
                                {post.username?.charAt(0).toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex items-center gap-1">
                            <span className="text-white font-semibold text-shadow">
                                {post.username}
                            </span>
                            {post.is_verified && (
                                <BadgeCheck className="w-4 h-4 text-primary fill-primary" />
                            )}
                        </div>
                    </button>

                    {/* Caption */}
                    <p className="text-white text-sm text-shadow mb-2 line-clamp-3">
                        {post.caption}
                    </p>

                    {/* Tags */}
                    {(post.position_tag || post.level) && (
                        <div className="flex gap-2 flex-wrap">
                            {post.position_tag && (
                                <span className="text-xs bg-white/20 backdrop-blur-sm px-2 py-1 rounded-full text-white">
                                    {post.position_tag}
                                </span>
                            )}
                            {post.level && (
                                <span className="text-xs bg-primary/80 px-2 py-1 rounded-full text-white">
                                    {post.level}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {/* Action Buttons (Right Side) - positioned above bottom nav */}
                <div className="absolute right-3 bottom-4 flex flex-col items-center gap-5 z-10">
                    {/* Like Button */}
                    <button 
                        onClick={handleLike}
                        className="flex flex-col items-center"
                        data-testid={`like-button-${post.id}`}
                    >
                        <div className={`w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center ${liked ? 'heart-animate' : ''}`}>
                            <Heart 
                                className={`w-7 h-7 ${liked ? 'text-red-500 fill-red-500' : 'text-white'}`} 
                            />
                        </div>
                        <span className="text-white text-xs mt-1 text-shadow font-medium">
                            {formatCount(likesCount)}
                        </span>
                    </button>

                    {/* Comment Button */}
                    <button 
                        onClick={() => setShowComments(true)}
                        className="flex flex-col items-center"
                        data-testid={`comment-button-${post.id}`}
                    >
                        <div className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                            <MessageCircle className="w-7 h-7 text-white" />
                        </div>
                        <span className="text-white text-xs mt-1 text-shadow font-medium">
                            {formatCount(post.comments_count)}
                        </span>
                    </button>

                    {/* Share Button */}
                    <button 
                        className="flex flex-col items-center"
                        data-testid={`share-button-${post.id}`}
                    >
                        <div className="w-12 h-12 rounded-full bg-black/30 backdrop-blur-sm flex items-center justify-center">
                            <Share2 className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-white text-xs mt-1 text-shadow font-medium">
                            Share
                        </span>
                    </button>
                </div>
            </div>

            <CommentsModal 
                open={showComments}
                onClose={() => setShowComments(false)}
                postId={post.id}
            />
        </>
    );
};

export default PostCard;
